import { useTranslation } from 'react-i18next';
import { FunctionComponent, useState } from 'react';
import { Button, Card, Col, Divider, Flex, Row, Table, TableProps } from 'antd';

import DisabledElement from 'components/disabledElement/disabledElement';
import TableTotal from 'components/tableTotal/tableTotal';
import NoData from 'components/noData/noData';

import { onExportProfitCapitalizationFractions, onGetProfitCapitalizationFractions } from '../services/profitCapitalization';
import NotificationService from 'core/services/notification';
import { isRTL } from 'core/services/language';
import { dateFormat } from 'utils/dateFormat';
import { downloadFile } from 'utils/downloadFile';

import { ProfitCapitalizationFraction } from '../models/profitCapitalization';
import { TreasurySharesAcquisition, TreasurySharesAcquisitionSeller } from '../models/treasurySharesAcquisition';
import { onGetRaiseCapital } from '../services/raiseCapitals';
import StatusComponent from 'components/status/status';
import SadadInvoiceComponent from 'modules/main/actions/sadadInvoice';
import { onExportTreasurySharesAcquisitionSellers } from '../services/treasurySharesAcquisition';

interface TreasurySharesAcquisitionComponentProps {
	treasurySharesAcquisition: TreasurySharesAcquisition;
}

const TreasurySharesAcquisitionComponent: FunctionComponent<TreasurySharesAcquisitionComponentProps> = ({ treasurySharesAcquisition }) => {
	const { t } = useTranslation();

	const [isLoading, setIsLoading] = useState<boolean>(false);
	const [isExporting, setIsExporting] = useState<boolean>(false);
	const [sellers, setSellers] = useState<TreasurySharesAcquisitionSeller[]>(treasurySharesAcquisition?.sellers?.sellers || []);

	const [currentPage, setCurrentPage] = useState<number>(1);
	const [totalRecords] = useState<number>(treasurySharesAcquisition?.sellers?.pagination?.total || 0);

	const columns: TableProps<TreasurySharesAcquisitionSeller>['columns'] = [
		{
			title: t('COMMON.NATIONAL_ID'),
			dataIndex: 'national_id',
			key: 'national_id',
		},

		{
			title: t('COMMON.NAME'),
			dataIndex: 'name',
			key: 'name',
			// render: (name) => <span style={{ textWrap: 'wrap' }}>{isRTL() ? name.ar || name.en : name.en || name.ar}</span>,
		},

		{
			title: t('COMMON.SHARES'),
			dataIndex: 'shares',
			key: 'shares',
			width: 150,
		},
		{
			title: t('ACTIONS.IBAN'),
			dataIndex: 'iban',
			key: 'iban',
			render: (iban) => (iban ? iban : t('COMMON.N_A')),
		},
	];

	const fetchMoreData = async (page: number) => {
		try {
			setIsLoading(true);

			const response = await onGetRaiseCapital(treasurySharesAcquisition.action.id, page);
			setSellers((response as TreasurySharesAcquisition).sellers?.sellers || []);

			setCurrentPage(page);
			setIsLoading(false);
		} catch (error) {
			NotificationService.error(t('COMMON.ERROR'), t('COMMON.FETCH_ERROR'));
			setIsLoading(false);
		}
	};

	const exportToExcelHandler = async () => {
		try {
			setIsExporting(true);

			const response = await onExportTreasurySharesAcquisitionSellers(treasurySharesAcquisition.action.id);

			if (response.excel_file_path)
				await downloadFile(response.excel_file_path, isRTL() ? 'بائعي أسهم الخزانة.xlsx' : 'treasury_shares_acquisition_sellers.xlsx');
			else NotificationService.error(t('COMMON.ERROR'), response.message || t('COMMON.FETCH_ERROR'));

			setIsExporting(false);
		} catch (error) {
			NotificationService.error(t('COMMON.ERROR'), t('COMMON.FETCH_ERROR'));
			setIsExporting(false);
		}
	};

	return (
		<>
			<Row gutter={[16, 16]} className="mt-4">
				<Col span={24} md={12} lg={8}>
					<DisabledElement
						label={t('ACTIONS.TOTAL_TREASURY_SHARES_TO_BE_ISSUED')}
						style={{ paddingBlock: 4 }}
						value={treasurySharesAcquisition?.action.number_of_treasury_shares + ' ' + t('COMMON.SHARES')}
						addonAfter={
							<StatusComponent
								small
								text={`${t('ACTIONS.BOUGHT')}: ${treasurySharesAcquisition?.action.bought_shares_count} ${t('COMMON.SHARES')}`}
								color={
									treasurySharesAcquisition?.action.bought_shares_count == 0
										? 'red'
										: treasurySharesAcquisition?.action.bought_shares_count == treasurySharesAcquisition?.action?.number_of_treasury_shares
										? 'green'
										: 'orange'
								}
							/>
						}
					/>
				</Col>

				<Col span={24} md={12} lg={5}>
					<DisabledElement label={t('ACTIONS.PRICE')} value={treasurySharesAcquisition?.action.share_price} showPrice />
				</Col>

				<Col span={24} md={12} lg={6}>
					<DisabledElement value={dateFormat(treasurySharesAcquisition?.action.starting_date, true)} label={t('ACTIONS.STARTING_DATE_BUY')} />
				</Col>

				<Col span={24} md={12} lg={5}>
					<DisabledElement
						label={t('ACTIONS.REMAINING_DAYS')}
						value={treasurySharesAcquisition.action.remaining_days}
						addonAfter={<span className="me-2">{t('ACTIONS.DAYS')}</span>}
					/>
				</Col>
			</Row>

			<Divider />

			<Card>
				{treasurySharesAcquisition?.payments.map((payment, index) => (
					<SadadInvoiceComponent key={index} payment={payment} />
				))}
			</Card>

			<Divider />

			<Flex align="center" justify="space-between" className="mb-3">
				<h6 className="mb-0 fw-medium">{t('ACTIONS.SELLERS')}</h6>

				<Button type="primary" size="middle" loading={isExporting} onClick={exportToExcelHandler}>
					{t('COMMON.EXPORT_TO_EXCEL')}
				</Button>
			</Flex>

			<Table
				columns={columns}
				dataSource={sellers}
				scroll={{ x: 600 }}
				rowKey="id"
				loading={isLoading}
				bordered
				pagination={{
					pageSize: 15,
					total: totalRecords,
					showSizeChanger: false,
					current: currentPage,
					onChange: fetchMoreData,
					showTotal: (total) => <TableTotal total={total} />,
				}}
				locale={{ emptyText: <NoData /> }}
				sticky
			/>
		</>
	);
};

export default TreasurySharesAcquisitionComponent;
